import React from "react";
import { Filter, Loader2, RefreshCw } from "lucide-react";
import { Button } from "../ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Label } from "../ui/label";

const ANY = "__any__"; // sentinel for "no filter"

const FILTERS = [
  { key: "sector", label: "Sector", optKey: "sectors" },
  { key: "market_cap", label: "Market Cap", optKey: "market_caps" },
  { key: "pe", label: "P/E Ratio", optKey: "pe" },
  { key: "performance", label: "Performance", optKey: "performance" },
  { key: "dividend", label: "Dividend", optKey: "dividend" },
  { key: "country", label: "Country", optKey: "country" },
  { key: "avg_volume", label: "Avg Volume", optKey: "avg_volume" },
];

/**
 * Sticky filter sidebar for the Stock Screener page.
 *
 * Renders the 7 Finviz filter dropdowns, the sort selector, an active-filter
 * count, reset button and the Apply button. Pure controlled component —
 * parent owns the filters/sortBy state.
 */
export default function ScreenerFilters({
  filters,
  setFilters,
  sortBy,
  setSortBy,
  options,
  loading,
  runScreen,
}) {
  const activeCount = Object.values(filters).filter(Boolean).length;
  const resetFilters = () => {
    setFilters({
      sector: "",
      market_cap: "",
      pe: "",
      performance: "",
      dividend: "",
      country: "",
      avg_volume: "",
    });
    setSortBy("marketcap_desc");
  };
  return (
    <aside
      className="rounded-2xl bg-white border border-[#EAE6E1] p-5 h-fit lg:sticky lg:top-20"
      data-testid="screener-filters"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-[#D18E63]" strokeWidth={1.5} />
          <span className="font-heading text-lg text-[#2A2F2B]">Filters</span>
          {activeCount > 0 && (
            <span
              data-testid="screener-active-filter-count"
              className="px-1.5 py-0.5 rounded-full bg-[#547A8F] text-white text-[10px] font-mono-ticker"
            >
              {activeCount}
            </span>
          )}
        </div>
        {activeCount > 0 && (
          <button
            onClick={resetFilters}
            data-testid="screener-reset-filters"
            className="text-xs text-[#547A8F] hover:underline"
          >
            Reset
          </button>
        )}
      </div>

      <div className="space-y-3">
        {FILTERS.map(({ key, label, optKey }) => (
          <div key={key}>
            <Label className="text-[10px] uppercase tracking-[0.2em] text-[#6B7068]">
              {label}
            </Label>
            <Select
              value={filters[key] || ANY}
              onValueChange={(v) =>
                setFilters((f) => ({ ...f, [key]: v === ANY ? "" : v }))
              }
            >
              <SelectTrigger
                className="mt-1 rounded-xl border-[#EAE6E1] bg-white text-sm"
                data-testid={`screener-filter-${key}`}
              >
                <SelectValue placeholder="Any" />
              </SelectTrigger>
              <SelectContent className="rounded-xl">
                <SelectItem value={ANY}>Any</SelectItem>
                {(options?.[optKey] || []).map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ))}

        <div className="pt-2 border-t border-[#EAE6E1]">
          <Label className="text-[10px] uppercase tracking-[0.2em] text-[#6B7068]">
            Sort by
          </Label>
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger
              className="mt-1 rounded-xl border-[#EAE6E1] bg-white text-sm"
              data-testid="screener-sort"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="rounded-xl">
              {(options?.sort || []).map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <Button
        onClick={runScreen}
        data-testid="screener-run-btn"
        disabled={loading}
        className="w-full mt-5 rounded-full bg-[#2A2F2B] hover:bg-[#547A8F] text-white"
      >
        {loading ? (
          <>
            <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Searching...
          </>
        ) : (
          <>
            <RefreshCw className="w-4 h-4 mr-2" strokeWidth={1.5} /> Apply filters
          </>
        )}
      </Button>
    </aside>
  );
}
